import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Save, Loader2, Pencil } from 'lucide-react';

const Field = ({ label, value, onChange, type = 'number', step, prefix, suffix, testId }) => (
  <div>
    <label className="flex items-center gap-1.5 text-xs text-zinc-400 mb-1">
      {label}
      <Pencil className="w-2.5 h-2.5 text-amber-500/70" />
    </label>
    <div className="relative">
      {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{prefix}</span>}
      <input
        type={type}
        step={step || 'any'}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        className={`w-full bg-zinc-800/60 border-2 border-amber-500/30 rounded-md text-sm text-zinc-200 font-mono py-1.5 
          ${prefix ? 'pl-6' : 'pl-2.5'} ${suffix ? 'pr-8' : 'pr-2.5'} 
          focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/20
          hover:border-amber-500/50 transition-colors`}
        data-testid={testId}
      />
      {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{suffix}</span>}
    </div>
  </div>
);

const DealForm = ({ data, onChange, label }) => (
  <div className="space-y-4">
    <h4 className="text-sm font-medium text-zinc-200 font-['Manrope']">{label}</h4>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <Field label="Purchase Price" value={data?.purchase_price} onChange={(v) => onChange({ ...data, purchase_price: v })} prefix="$" testId={`${label}-purchase-price`} />
      <Field label="Working Capital" value={data?.working_capital} onChange={(v) => onChange({ ...data, working_capital: v })} prefix="$" />
      <Field label="Closing Fees" value={data?.closing_fees} onChange={(v) => onChange({ ...data, closing_fees: v })} prefix="$" />
      <Field label="SDE" value={data?.sde} onChange={(v) => onChange({ ...data, sde: v })} prefix="$" />
      <Field label="Buyer Salary" value={data?.buyer_salary} onChange={(v) => onChange({ ...data, buyer_salary: v })} prefix="$" />
      <Field label="SBA Loan %" value={data?.sba_loan_pct} onChange={(v) => onChange({ ...data, sba_loan_pct: v })} step="0.01" />
      <Field label="SBA Rate" value={data?.sba_rate} onChange={(v) => onChange({ ...data, sba_rate: v })} step="0.01" />
      <Field label="SBA Years" value={data?.sba_years} onChange={(v) => onChange({ ...data, sba_years: v })} />
      <Field label="Seller Note %" value={data?.seller_note_pct} onChange={(v) => onChange({ ...data, seller_note_pct: v })} step="0.01" />
      <Field label="Seller Note Rate" value={data?.seller_note_rate} onChange={(v) => onChange({ ...data, seller_note_rate: v })} step="0.01" />
      <Field label="Seller Note Years" value={data?.seller_note_years} onChange={(v) => onChange({ ...data, seller_note_years: v })} />
      <Field label="Equity %" value={data?.equity_pct} onChange={(v) => onChange({ ...data, equity_pct: v })} step="0.01" />
    </div>
  </div>
);

const emptyPnl = { year: '', revenue: 0, cogs: 0, operating_expenses: 0, net_income: 0 };
const emptySde = { label: '', net_income: 0, officer_salary: 0, interest_expense: 0, depreciation: 0, entertainment: 0, other_addbacks: 0 };
const emptyBs = { cash: 0, inventory: 0, accounts_receivable: 0, accounts_payable: 0, current_debt_portion: 0, seller_note_outstanding: 0, sba_loan_outstanding: 0, equity_injection: 0 };
const emptyDeal = { purchase_price: 0, working_capital: 0, closing_fees: 0, sde: 0, buyer_salary: 75000, sba_loan_pct: 0, sba_rate: 0.10, sba_years: 10, seller_note_pct: 0.6, seller_note_rate: 0.06, seller_note_years: 5, equity_pct: 0.4 };

export default function ManualEntryForm({ onSave, loading, existingData, analysisName }) {
  // Initialize with existing data if available, otherwise use defaults
  const [formData, setFormData] = useState(() => {
    if (existingData) {
      return {
        name: analysisName || existingData.name || 'Analysis',
        description: existingData.description || '',
        seller_deal: existingData.seller_deal || { ...emptyDeal },
        buyer_deal: existingData.buyer_deal || { ...emptyDeal },
        comparable_deal: existingData.comparable_deal || { ...emptyDeal },
        pnl_data: existingData.pnl_data?.length > 0 ? existingData.pnl_data : [{ ...emptyPnl, year: '2024' }, { ...emptyPnl, year: '2025' }],
        sde_entries: existingData.sde_entries?.length > 0 ? existingData.sde_entries : [{ ...emptySde, label: '2024' }],
        seller_balance_sheet: existingData.seller_balance_sheet || { ...emptyBs },
        buyer_balance_sheet: existingData.buyer_balance_sheet || { ...emptyBs },
        inventory_entries: existingData.inventory_entries?.length > 0 ? existingData.inventory_entries : [{ label: '2024', cogs: 0, inventory: 0 }],
        dcf_assumptions: existingData.dcf_assumptions || { discount_rate: 0.15, growth_rates: [0.03, 0.03, 0.03, 0.03, 0.03], exit_multiple: 3.0, cogs_pct: 0.60, opex_pct: 0.23, buyer_salary: 75000, tax_rate: 0, capex_pct: 0.025, working_capital_pct: 0.10, base_revenue: 0 },
        scenarios: existingData.scenarios?.length > 0 ? existingData.scenarios : [
          { name: 'Flat (Status Quo)', label: 'A', growth_rates: [0, 0, 0, 0, 0], exit_multiple: 3.0 },
          { name: 'Recovery', label: 'B', growth_rates: [0.10, 0.10, 0.05, 0.05, 0.05], exit_multiple: 3.0 },
          { name: 'Continued Decline', label: 'C', growth_rates: [-0.10, -0.05, 0, 0, 0], exit_multiple: 3.0 },
        ],
        // Preserve other fields that might exist
        working_capital: existingData.working_capital,
        manual_comps: existingData.manual_comps,
      };
    }
    return {
      name: 'New Analysis',
      description: '',
      seller_deal: { ...emptyDeal },
      buyer_deal: { ...emptyDeal },
      comparable_deal: { ...emptyDeal },
      pnl_data: [{ ...emptyPnl, year: '2024' }, { ...emptyPnl, year: '2025' }],
      sde_entries: [{ ...emptySde, label: '2024' }],
      seller_balance_sheet: { ...emptyBs },
      buyer_balance_sheet: { ...emptyBs },
      inventory_entries: [{ label: '2024', cogs: 0, inventory: 0 }],
      dcf_assumptions: { discount_rate: 0.15, growth_rates: [0.03, 0.03, 0.03, 0.03, 0.03], exit_multiple: 3.0, cogs_pct: 0.60, opex_pct: 0.23, buyer_salary: 75000, tax_rate: 0, capex_pct: 0.025, working_capital_pct: 0.10, base_revenue: 0 },
      scenarios: [
        { name: 'Flat (Status Quo)', label: 'A', growth_rates: [0, 0, 0, 0, 0], exit_multiple: 3.0 },
        { name: 'Recovery', label: 'B', growth_rates: [0.10, 0.10, 0.05, 0.05, 0.05], exit_multiple: 3.0 },
        { name: 'Continued Decline', label: 'C', growth_rates: [-0.10, -0.05, 0, 0, 0], exit_multiple: 3.0 },
      ],
    };
  });

  const updateField = useCallback((path, value) => {
    setFormData(prev => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  }, []);

  const updateArrayItem = useCallback((arrayPath, idx, value) => {
    setFormData(prev => {
      const next = { ...prev };
      next[arrayPath] = [...next[arrayPath]];
      next[arrayPath][idx] = value;
      return next;
    });
  }, []);

  const addArrayItem = useCallback((arrayPath, template) => {
    setFormData(prev => ({ ...prev, [arrayPath]: [...prev[arrayPath], { ...template }] }));
  }, []);

  const removeArrayItem = useCallback((arrayPath, idx) => {
    setFormData(prev => ({ ...prev, [arrayPath]: prev[arrayPath].filter((_, i) => i !== idx) }));
  }, []);

  const handleSave = () => {
    if (onSave) onSave(formData);
  };

  return (
    <div className="space-y-4" data-testid="manual-entry-form">
      {/* Manual Entry Legend */}
      <div className="flex items-center gap-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-amber-500/50 bg-zinc-800/60" />
          <span className="text-xs text-zinc-400">Amber border = editable field</span>
        </div>
        <div className="flex items-center gap-2">
          <Pencil className="w-3 h-3 text-amber-500/70" />
          <span className="text-xs text-zinc-400">Pencil icon = manual entry</span>
        </div>
      </div>

      {/* Analysis Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Analysis Name" value={formData.name} onChange={(v) => updateField('name', v)} type="text" testId="manual-analysis-name" />
        <Field label="Description" value={formData.description} onChange={(v) => updateField('description', v)} type="text" />
      </div>

      <Tabs defaultValue="deals" className="w-full">
        <TabsList className="bg-zinc-800/60 border border-zinc-700/50 w-full justify-start flex-wrap h-auto p-1 gap-1">
          <TabsTrigger value="deals" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">Deals</TabsTrigger>
          <TabsTrigger value="pnl" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">P&L</TabsTrigger>
          <TabsTrigger value="sde" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">SDE</TabsTrigger>
          <TabsTrigger value="balance" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">Inventory</TabsTrigger>
          <TabsTrigger value="dcf" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">DCF</TabsTrigger>
          <TabsTrigger value="scenarios" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 text-xs">Scenarios</TabsTrigger>
        </TabsList>

        {/* Deals Tab */}
        <TabsContent value="deals" className="space-y-4 mt-4">
          <DealForm data={formData.seller_deal} onChange={(v) => updateField('seller_deal', v)} label="Seller Deal" />
          <div className="border-t border-zinc-800/60" />
          <DealForm data={formData.buyer_deal} onChange={(v) => updateField('buyer_deal', v)} label="Buyer Deal" />
          <div className="border-t border-zinc-800/60" />
          <DealForm data={formData.comparable_deal} onChange={(v) => updateField('comparable_deal', v)} label="Comparable Deal" />
        </TabsContent>

        {/* P&L Tab */}
        <TabsContent value="pnl" className="space-y-3 mt-4">
          {formData.pnl_data.map((entry, idx) => (
            <Card key={idx} className="bg-zinc-900/40 border-zinc-800/50">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{entry.year || `Year ${idx + 1}`}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('pnl_data', idx)} className="text-zinc-500 hover:text-red-400 h-6 w-6 p-0">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Field label="Year" value={entry.year} onChange={(v) => updateArrayItem('pnl_data', idx, { ...entry, year: v })} type="text" />
                  <Field label="Revenue" value={entry.revenue} onChange={(v) => updateArrayItem('pnl_data', idx, { ...entry, revenue: v })} prefix="$" />
                  <Field label="COGS" value={entry.cogs} onChange={(v) => updateArrayItem('pnl_data', idx, { ...entry, cogs: v })} prefix="$" />
                  <Field label="Operating Expenses" value={entry.operating_expenses} onChange={(v) => updateArrayItem('pnl_data', idx, { ...entry, operating_expenses: v })} prefix="$" />
                  <Field label="Net Income" value={entry.net_income} onChange={(v) => updateArrayItem('pnl_data', idx, { ...entry, net_income: v })} prefix="$" />
                </div>
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" size="sm" onClick={() => addArrayItem('pnl_data', emptyPnl)} className="border-zinc-700 text-zinc-400" data-testid="add-pnl-row">
            <Plus className="w-3 h-3 mr-1" /> Add Year
          </Button>
        </TabsContent>

        {/* SDE Tab */}
        <TabsContent value="sde" className="space-y-3 mt-4">
          {formData.sde_entries.map((entry, idx) => (
            <Card key={idx} className="bg-zinc-900/40 border-zinc-800/50">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400">{entry.label || `Entry ${idx + 1}`}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => removeArrayItem('sde_entries', idx)} className="text-zinc-500 hover:text-red-400 h-6 w-6 p-0">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Field label="Label" value={entry.label} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, label: v })} type="text" />
                  <Field label="Net Income" value={entry.net_income} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, net_income: v })} prefix="$" />
                  <Field label="Officer Salary" value={entry.officer_salary} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, officer_salary: v })} prefix="$" />
                  <Field label="Interest Expense" value={entry.interest_expense} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, interest_expense: v })} prefix="$" />
                  <Field label="Depreciation" value={entry.depreciation} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, depreciation: v })} prefix="$" />
                  <Field label="Entertainment" value={entry.entertainment} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, entertainment: v })} prefix="$" />
                  <Field label="Other Add-Backs" value={entry.other_addbacks} onChange={(v) => updateArrayItem('sde_entries', idx, { ...entry, other_addbacks: v })} prefix="$" />
                </div>
              </CardContent>
            </Card>
          ))}
          <Button variant="outline" size="sm" onClick={() => addArrayItem('sde_entries', emptySde)} className="border-zinc-700 text-zinc-400" data-testid="add-sde-row">
            <Plus className="w-3 h-3 mr-1" /> Add SDE Entry
          </Button>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance" className="space-y-4 mt-4">
          <div>
            <h4 className="text-sm font-medium text-zinc-200 mb-3 font-['Manrope']">Seller Balance Sheet</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.keys(emptyBs).map(k => (
                <Field key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={formData.seller_balance_sheet[k]}
                  onChange={(v) => updateField(`seller_balance_sheet.${k}`, v)} prefix="$" />
              ))}
            </div>
          </div>
          <div className="border-t border-zinc-800/60" />
          <div>
            <h4 className="text-sm font-medium text-zinc-200 mb-3 font-['Manrope']">Buyer Balance Sheet</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.keys(emptyBs).map(k => (
                <Field key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={formData.buyer_balance_sheet[k]}
                  onChange={(v) => updateField(`buyer_balance_sheet.${k}`, v)} prefix="$" />
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-3 mt-4">
          {formData.inventory_entries.map((entry, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 items-end">
              <Field label="Label" value={entry.label} onChange={(v) => updateArrayItem('inventory_entries', idx, { ...entry, label: v })} type="text" />
              <Field label="COGS" value={entry.cogs} onChange={(v) => updateArrayItem('inventory_entries', idx, { ...entry, cogs: v })} prefix="$" />
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <Field label="Inventory" value={entry.inventory} onChange={(v) => updateArrayItem('inventory_entries', idx, { ...entry, inventory: v })} prefix="$" />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeArrayItem('inventory_entries', idx)} className="text-zinc-500 hover:text-red-400 h-8 w-8 p-0 shrink-0">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addArrayItem('inventory_entries', { label: '', cogs: 0, inventory: 0 })} className="border-zinc-700 text-zinc-400">
            <Plus className="w-3 h-3 mr-1" /> Add Inventory Entry
          </Button>
        </TabsContent>

        {/* DCF Tab */}
        <TabsContent value="dcf" className="space-y-3 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Base Revenue" value={formData.dcf_assumptions.base_revenue} onChange={(v) => updateField('dcf_assumptions.base_revenue', v)} prefix="$" testId="manual-dcf-base-rev" />
            <Field label="Discount Rate" value={formData.dcf_assumptions.discount_rate} onChange={(v) => updateField('dcf_assumptions.discount_rate', v)} step="0.005" />
            <Field label="Exit Multiple" value={formData.dcf_assumptions.exit_multiple} onChange={(v) => updateField('dcf_assumptions.exit_multiple', v)} step="0.1" testId="manual-dcf-exit-multiple" />
            <Field label="COGS %" value={formData.dcf_assumptions.cogs_pct} onChange={(v) => updateField('dcf_assumptions.cogs_pct', v)} step="0.005" />
            <Field label="OpEx %" value={formData.dcf_assumptions.opex_pct} onChange={(v) => updateField('dcf_assumptions.opex_pct', v)} step="0.005" />
            <Field label="CapEx %" value={formData.dcf_assumptions.capex_pct} onChange={(v) => updateField('dcf_assumptions.capex_pct', v)} step="0.005" testId="manual-dcf-capex-pct" />
            <Field label="Working Capital %" value={formData.dcf_assumptions.working_capital_pct} onChange={(v) => updateField('dcf_assumptions.working_capital_pct', v)} step="0.01" testId="manual-dcf-wc-pct" />
            <Field label="Buyer Salary" value={formData.dcf_assumptions.buyer_salary} onChange={(v) => updateField('dcf_assumptions.buyer_salary', v)} prefix="$" />
            <Field label="Tax Rate" value={formData.dcf_assumptions.tax_rate} onChange={(v) => updateField('dcf_assumptions.tax_rate', v)} step="0.01" />
          </div>
          <div className="mt-3">
            <label className="block text-xs text-zinc-400 mb-2">Growth Rates (Year 1-5)</label>
            <div className="grid grid-cols-5 gap-2">
              {formData.dcf_assumptions.growth_rates.map((g, i) => (
                <Field key={i} label={`Yr ${i + 1}`} value={g}
                  onChange={(v) => {
                    const rates = [...formData.dcf_assumptions.growth_rates];
                    rates[i] = v;
                    updateField('dcf_assumptions.growth_rates', rates);
                  }} step="0.005" />
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-3 mt-4">
          {formData.scenarios.map((sc, sIdx) => (
            <Card key={sIdx} className="bg-zinc-900/40 border-zinc-800/50">
              <CardContent className="p-3">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Field label="Scenario Name" value={sc.name} onChange={(v) => {
                    const s = [...formData.scenarios]; s[sIdx] = { ...s[sIdx], name: v };
                    updateField('scenarios', s);
                  }} type="text" />
                  <Field label="Exit Multiple" value={sc.exit_multiple} onChange={(v) => {
                    const s = [...formData.scenarios]; s[sIdx] = { ...s[sIdx], exit_multiple: v };
                    updateField('scenarios', s);
                  }} step="0.1" />
                </div>
                <label className="block text-xs text-zinc-400 mb-1">Growth Rates (Year 1-5)</label>
                <div className="grid grid-cols-5 gap-2">
                  {sc.growth_rates.map((g, yIdx) => (
                    <Field key={yIdx} label={`Yr ${yIdx + 1}`} value={g}
                      onChange={(v) => {
                        const s = [...formData.scenarios];
                        const rates = [...s[sIdx].growth_rates]; rates[yIdx] = v;
                        s[sIdx] = { ...s[sIdx], growth_rates: rates };
                        updateField('scenarios', s);
                      }} step="0.01" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={loading}
        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
        data-testid="manual-save-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save & Calculate
      </Button>
    </div>
  );
}
